/**
 * Dates in the conversation: the stamp next to each message ("2 oct 9:52 p.m.")
 * and the day separators ("Hoy", "Ayer", "Sábado", "15 sep").
 *
 * The copy (month and weekday names, "a.m."/"p.m.", the patterns) comes from
 * `chat/chat.json` → `bubbleDate`, so these helpers only take numbers apart and
 * pick a pattern. The year shows only when it is not the current one.
 */

import { getDateTimeFormatter } from 'src/utils/intlFormatters';

export type tBubbleDateLanguage = 'es' | 'en' | 'br';

export type tBubbleDateCopy = {
  months: string[];
  weekdays: string[];
  today: string;
  yesterday: string;
  am: string;
  pm: string;
  /** "12" for "9:52 p.m.", "24" for "21:52". */
  clock: string;
  /** Interpolated with {{day}}, {{month}}, {{year}} and {{time}}. */
  stamp: string;
  stampWithYear: string;
  /** Interpolated with {{day}}, {{month}} and {{year}}. */
  day: string;
  dayWithYear: string;
};

const INTL_LOCALE: Record<tBubbleDateLanguage, string> = {
  es: 'es-419',
  en: 'en-US',
  br: 'pt-BR',
};

/**
 * Production locks dates to Spanish, like moment in `src/i18n.ts`; other
 * environments follow the active language. Anything unknown falls back to es.
 */
export function resolveBubbleDateLanguage(
  language: string | undefined,
  env: string | undefined,
): tBubbleDateLanguage {
  if (env === 'production') return 'es';
  const base = (language ?? '').toLowerCase().split('-')[0];
  if (base === 'en') return 'en';
  if (base === 'br' || base === 'pt') return 'br';
  return 'es';
}

function fill(pattern: string, values: Record<string, string | number>) {
  return pattern.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    values[key] === undefined ? '' : String(values[key]),
  );
}

/** "9:52 p.m." (12 h, non-breaking space before the suffix) or "21:52". */
export function formatBubbleTime(date: Date, copy: tBubbleDateCopy) {
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  if (copy.clock === '24') {
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }
  const hours12 = hours % 12 === 0 ? 12 : hours % 12;
  const suffix = hours >= 12 ? copy.pm : copy.am;
  return suffix ? `${hours12}:${minutes}\u00a0${suffix}` : `${hours12}:${minutes}`;
}

/** The stamp next to a message: "2 oct 9:52 p.m." or "2 oct 2025, 9:52 p.m.". */
export function formatBubbleStamp(
  value: string | Date,
  options: { copy: tBubbleDateCopy; now?: Date },
) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = options.now ?? new Date();
  const { copy } = options;
  const values = {
    day: date.getDate(),
    month: copy.months[date.getMonth()] ?? '',
    year: date.getFullYear(),
    time: formatBubbleTime(date, copy),
  };
  return fill(
    date.getFullYear() === now.getFullYear() ? copy.stamp : copy.stampWithYear,
    values,
  );
}

/** The same moment in words, for screen readers: "2 de octubre de 2026, 9:52 p.m.". */
export function spokenBubbleStamp(
  value: string | Date,
  language: tBubbleDateLanguage,
) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return getDateTimeFormatter(INTL_LOCALE[language], {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(date);
}

/** Local calendar day as "YYYY-MM-DD", the key that splits the conversation into days. */
export function localDayKey(value: string | Date) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Whole calendar days between two local dates (DST-safe: compares midnights by date parts). */
export function calendarDayDiff(from: Date, to: Date) {
  const start = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const end = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((end - start) / 86_400_000);
}

/**
 * The day separator label: "Hoy", "Ayer", the weekday for 2 to 6 days back
 * (7 days back would repeat today's weekday), then "15 sep" or "28 dic 2025".
 */
export function formatDayLabel(
  value: string | Date,
  options: { copy: tBubbleDateCopy; now?: Date },
) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = options.now ?? new Date();
  const { copy } = options;
  const diff = calendarDayDiff(date, now);
  if (diff === 0) return copy.today;
  if (diff === 1) return copy.yesterday;
  if (diff >= 2 && diff <= 6) return copy.weekdays[date.getDay()] ?? '';
  const values = {
    day: date.getDate(),
    month: copy.months[date.getMonth()] ?? '',
    year: date.getFullYear(),
  };
  return fill(
    date.getFullYear() === now.getFullYear() ? copy.day : copy.dayWithYear,
    values,
  );
}
